import type React from 'react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Modal } from '../../../../../customComponents/Modal'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'
import { TrackFocusBuilding } from '../../../../../../services/AnnualPlanService'
import { ActivityEditSession } from '../../ActivityEditSession'
import { PriorityFourActivityName } from '../../../../../../models/PriorityFourActivityName'
import { type FocusBuilding } from '../../../../../../models/FocusBuilding'
import { type TrackFocusBuildingRequest } from '../../../../../../models/requestObjects/priorityFour/focusBuilding/FocusBuildingRequestObjects'
import { type FocusBuildingProgress } from '../../../../../../models/FocusBuildingProgress'
import { isValidPositiveNumber } from '../../../../../../utils/isValidNumber'

type TrackFocusBuildingFormValues = FocusBuildingProgress & {
  actualBudget: string
}

interface Props {
  initialValues: FocusBuilding
  onClose: () => void
  onSaved: () => void
}

export const TrackFocusBuildingModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<TrackFocusBuildingFormValues>({
    inProgress: props.initialValues.progress?.inProgress ?? false,
    programExpanded: props.initialValues.progress?.programExpanded ?? false,
    notStarted: props.initialValues.progress?.notStarted ?? false,
    newProgramEstablished: props.initialValues.progress?.newProgramEstablished ?? false,
    actualBudget: props.initialValues.actualBudget != null ? String(props.initialValues.actualBudget) : ''
  })
  const [submitted, setIsSubmitted] = useState(false)

  const hasValidBudget = formValues.actualBudget == null ||
    formValues.actualBudget === '' ||
    isValidPositiveNumber(formValues.actualBudget)

  const validForm = (values: TrackFocusBuildingFormValues): boolean => {
    return (values.inProgress ||
      values.notStarted ||
      values.programExpanded ||
      values.newProgramEstablished) && hasValidBudget
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (!validForm(formValues)) {
      toast.error('Please complete all required fields')
      return
    }

    const trackRequest: TrackFocusBuildingRequest = {
      id: props.initialValues.id,
      progress: {
        programExpanded: formValues.programExpanded,
        newProgramEstablished: formValues.newProgramEstablished,
        notStarted: formValues.notStarted,
        inProgress: formValues.inProgress
      },
      actualBudget: Number(formValues.actualBudget)
    }

    const { success, errors } = await TrackFocusBuilding(trackRequest)

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success('Focus Building Tracked')

    props.onClose()
    props.onSaved()
  }

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.checked
    })
  }

  return <Modal
    title='Focus Buildings'
    open={true}
    data-testid='trackFocusBuildingsModal'
    width='480px'
    confirmButtonText='Save'
    cancelButtonText='Cancel'
    onClose={props.onClose}
    onConfirm={handleSubmit}
    bodyContent={
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <FormControl component="fieldset" variant="standard">
          <Typography variant='h5'>Progress</Typography>
          <Typography>Please select your current progress.</Typography>
          <Typography sx={{ fontStyle: 'italic' }}>Select all that apply</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox checked={formValues.newProgramEstablished} onChange={handleCheck} name="newProgramEstablished" data-testid='newProgramEstablished' />
            }
              label="New program established"
            />
            <FormControlLabel
              control={
                <Checkbox checked={formValues.programExpanded} onChange={handleCheck} name="programExpanded" data-testid='programExpanded' />
            }
              label="Program expanded"
            />
            <FormControlLabel
              control={
                <Checkbox checked={formValues.inProgress} onChange={handleCheck} name="inProgress" data-testid='inProgress' />
            }
              label="In progress"
            />
            <FormControlLabel
              control={
                <Checkbox checked={formValues.notStarted} onChange={handleCheck} name="notStarted" data-testid='notStarted' />
            }
              label="Not started"
            />
          </FormGroup>
        </FormControl>

        <Typography variant='body1'>How much funding was actually used? <Box component="span" sx={{ fontStyle: 'italic' }}>optional</Box></Typography>

        <TextField
          id='actual-budget'
          sx={{ mt: '.75em', width: '250px' }}
          variant='filled'
          onChange={handleFieldUpdate}
          label={'Actual Budget'}
          data-testid='actualBudget'
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          name='actualBudget'
          error={submitted && !hasValidBudget}
          value={formValues.actualBudget}
        />

        {props.initialValues != null &&
          <ActivityEditSession
            activityId={props.initialValues.id}
            activityName={PriorityFourActivityName.FocusBuildings}
            onClose={props.onClose}
          />
            }
      </Box>
    }
  />
}
